/** Extra context about the failed request, attached to the thrown error. */
export type ServerErrorDetails = {
  status?: number;
  statusText?: string;
  url?: string;
  method?: string;
};

const MAX_PAYLOAD_LENGTH = 500;

/**
 * Thrown when the remote server answered with a non-successful status.
 *
 * Keeps the raw response payload in `data` while always exposing a readable
 * `message` (method, status, url and a serialized payload), so the failure
 * never surfaces as "[object Object]" in logs or in the Next.js error overlay.
 */
export class ServerError extends Error {
  readonly status?: number;
  readonly statusText?: string;
  readonly url?: string;
  readonly method?: string;
  readonly data: unknown;

  constructor(data: unknown, details: ServerErrorDetails = {}) {
    super(ServerError.buildMessage(data, details));

    this.name = "ServerError";
    this.status = details.status;
    this.statusText = details.statusText;
    this.url = details.url;
    this.method = details.method;
    this.data = data;
  }

  private static buildMessage(
    data: unknown,
    details: ServerErrorDetails
  ): string {
    const status = [details.status, details.statusText]
      .filter(Boolean)
      .join(" ");

    const payload = ServerError.stringify(data);

    const parts = [
      details.method,
      status,
      details.url,
      payload && `- ${payload}`,
    ].filter(Boolean);

    return parts.length ? parts.join(" ") : "Request failed";
  }

  private static stringify(data: unknown): string {
    if (data === null || data === undefined) {
      return "";
    }

    const text = typeof data === "string" ? data : ServerError.toJson(data);

    return text.length > MAX_PAYLOAD_LENGTH
      ? `${text.slice(0, MAX_PAYLOAD_LENGTH)}…`
      : text;
  }

  private static toJson(data: unknown): string {
    try {
      return JSON.stringify(data) ?? String(data);
    } catch {
      return String(data);
    }
  }
}
